import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from 'src/hooks';
import { SearchInput, SearchInputComponent } from './searchInput';

type HookProps = {
  inputClassName?: string;
  modalInputClassName?: string;
  placeholder?: string;
  initValue?: string;
  disabled?: boolean | null;
  onEnter?: () => void;
  isMiddle?: boolean;
};

type HookResult = {
  searchString: string;
  searchInput: React.ReactNode;
  modalSearchInput: React.ReactNode;
  applySearch: () => void;
  cancelSearch: () => void;
  clearSearch: () => void;
  focusInput: () => void;
};

export const useSearchInput = ({
  inputClassName,
  modalInputClassName,
  placeholder,
  initValue,
  disabled = false,
  onEnter,
  isMiddle = false,
}: HookProps): HookResult => {
  const [searchString, setSearchString] = useState(initValue || '');
  const [modalSearchString, setModalSearchString] = useState(searchString);
  const [throttleSearchString, setThrottleSearchString] =
    useState(searchString);
  const { t } = useLanguage();

  const modalInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setThrottleSearchString(searchString);
      setModalSearchString(searchString);
    }, 400);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchString]);

  return {
    searchString: throttleSearchString,
    searchInput: (
      <SearchInputComponent
        value={searchString}
        setValue={(value) => {
          setSearchString(value);
        }}
        placeholder={placeholder || t('general.search')}
        className={inputClassName}
        disabled={disabled}
        size={isMiddle ? 'middle' : 'medium'}
      />
    ),
    modalSearchInput: (
      <SearchInput
        value={modalSearchString}
        setValue={(value) => {
          setModalSearchString(value);
        }}
        placeholder={placeholder || t('general.search')}
        className={modalInputClassName}
        disabled={disabled}
        size="small"
        onEnter={() => {
          if (onEnter) {
            setThrottleSearchString(modalSearchString);
            setSearchString(modalSearchString);
            onEnter();
          }
        }}
        ref={modalInputRef}
      />
    ),
    applySearch: useCallback(() => {
      setThrottleSearchString(modalSearchString);
      setSearchString(modalSearchString);
    }, [modalSearchString]),
    cancelSearch: useCallback(() => {
      setModalSearchString(searchString);
    }, [searchString]),
    clearSearch: useCallback(() => {
      setSearchString('');
      setModalSearchString('');
      setThrottleSearchString('');
    }, []),
    focusInput: useCallback(() => {
      modalInputRef.current?.focus();
    }, []),
  };
};

export default useSearchInput;
